import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, BookHeart, Copy, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Button } from '@repo/ui/src/components/ui/button';
import { Skeleton } from '@repo/ui/src/components/ui/skeleton';
import { useIsMobile } from '@repo/ui/src/hooks/use-mobile';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@repo/ui/src/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import {
  Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle,
} from '@repo/ui/src/components/ui/drawer';
import { progressDisplayName, type StudentWithClient } from '@repo/shared-types';
import { AdminOnlyTooltip } from '../../../components/AdminOnlyTooltip';
import { useCan } from '../../../hooks/useCan';
import { useSubscription } from '../../../hooks/useSubscription';
import { displayUrl } from '../../../lib/displayUrl';
import { useUpgradeDialogStore } from '../../../stores/upgradeDialogStore';
import { useCreateProgressLink, useProgressLink } from '../../../hooks/useProgress';

/**
 * The Sổ liên lạc link block — Task 13. Self-contained (fetches its own
 * data), rendered by ProgressTab as its `aside`. The subscription's
 * `allowsProgressLink` flag has three readings, not two — unknown (still
 * loading) → render a loading skeleton; false → the FREE upsell; true → go
 * fetch the actual link. Treating "unknown" as "false" would flash the
 * upsell at every org, Pro/Center included, on every cold load of this tab.
 * Once the flag resolves, the states are:
 *  - Loading: the subscription query hasn't resolved yet.
 *  - Subscription error: the subscription fetch itself failed — no plan
 *    info at all, so this is a neutral retry state too, never the upsell.
 *  - FREE upsell: `allowsProgressLink` is false, OR the link read 403'd
 *    FEATURE_LOCKED regardless (a stale cached subscription after a
 *    same-session downgrade).
 *  - Neutral error: the plan DOES allow it but the link read failed some
 *    other way — never shown as the upsell, which would wrongly tell an
 *    admin their plan lacks a feature it has.
 *  - Not created: 200 with `data === null`.
 *  - Created: 200 with `{ url }`.
 * A MEMBER (no `manage:students`) sees all states; only the POST-backed
 * actions (create, regenerate) are gated, matching every other button on
 * this page — read-only actions (copy, open) stay enabled.
 */
export function ProgressLinkBlock({ student }: { student: StudentWithClient }) {
  const { t } = useTranslation('progress');
  const { t: tCommon } = useTranslation('common');
  const canManage = useCan('manage:students');
  // The regenerate confirm has no draft content of its own — unlike
  // ResponsiveSheet's Sheet/Drawer swap, a live viewport change flipping
  // which shape renders here mid-open loses nothing, so a plain `isMobile`
  // read (no mount latch) is enough (port guide §4.5: AlertDialog desktop,
  // Drawer on phone, destructive button first either way).
  const isMobile = useIsMobile();
  const {
    data: subscription, isPending: subscriptionPending, isError: subscriptionError,
    refetch: refetchSubscription,
  } = useSubscription();
  const { data: link, error, refetch, isPending: linkPending } = useProgressLink(student.id);
  const create = useCreateProgressLink(student.id);
  // The sample page is synthetic (`apps/web/app/lib/sampleProgress.ts`,
  // route `/hs/mau`): built relative to today on every request, so it never
  // goes stale and needs no demo org or per-environment token. The former
  // `VITE_DEMO_PROGRESS_URL` env var is gone for that reason.
  const demoProgressUrl = `${import.meta.env.VITE_WEB_URL || 'http://localhost:5173'}/hs/mau`;
  const [regenOpen, setRegenOpen] = useState(false);
  const displayName = progressDisplayName(student.name, student.nickname);

  // `error` is `fetchApi`'s Error, which carries `code` (lib/api.ts) but
  // isn't typed with it by default on a bare useQuery<T> — cast, same as
  // LoginPage.tsx:187 does for a mutation's error.
  const err = error as (Error & { code?: string }) | null;
  const planAllows = subscription?.plan.allowsProgressLink === true;
  // Only meaningful once the subscription itself has resolved — see the
  // `subscriptionPending`/`subscriptionError` early returns below, which run
  // BEFORE either of these is read. The upsell fires on "plan says no" OR a
  // residual FEATURE_LOCKED (the query is disabled whenever `!planAllows`,
  // so this only bites the rare case a stale cached subscription still let
  // the request through). Any OTHER error must NOT read as "needs Pro" —
  // that would tell an admin on Pro their plan lacks a feature it has.
  const locked = !planAllows || err?.code === 'FEATURE_LOCKED';
  const hasOtherError = planAllows && Boolean(err) && err?.code !== 'FEATURE_LOCKED';

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('link.copied', { name: displayName }));
    } catch {
      toast.error(t('link.copyFailed'));
    }
  };

  const createAndCopy = (regenerate: boolean) =>
    create.mutate(regenerate, {
      onSuccess: (d) => { void copy(d.url); setRegenOpen(false); },
    });

  const heading = (
    <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-surface-400">
      {t('link.title')}
    </p>
  );

  // Shared by both error states below (subscription fetch failed, or a
  // resolved-Pro plan whose link read still failed some other way) — same
  // box, different retry target.
  const errorBox = (onRetry: () => void) => (
    <div>
      {heading}
      <div className="mt-1.5 flex flex-col items-center gap-2 rounded-md border border-surface-200 bg-surface-0 p-4 text-center">
        <AlertTriangle className="h-5 w-5 text-destructive" strokeWidth={1.8} />
        <p className="text-[12.5px] leading-relaxed text-surface-500">{t('review.loadError')}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {tCommon('routeError.retry')}
        </Button>
      </div>
    </div>
  );

  // "Unknown" (the subscription query still resolving) must render as
  // loading, never as "locked" — see the docblock above.
  if (subscriptionPending) {
    return (
      <div>
        {heading}
        <div className="mt-1.5 space-y-2 rounded-md border border-surface-200 bg-surface-0 p-4">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
    );
  }

  // A failed subscription fetch carries no plan info — not a signal that
  // the plan disallows the feature, so this is never the upsell either.
  if (subscriptionError) {
    return errorBox(() => refetchSubscription());
  }

  if (locked) {
    return (
      <div>
        {heading}
        {/* attention, not brand: this is a ceiling that BLOCKS a capability,
            which is exactly what ochre is reserved for (INVARIANTS §6). */}
        <div className="mt-1.5 rounded-md border border-attention-border bg-attention-tint p-4">
          <p className="text-[13.5px] font-semibold text-attention-foreground">{t('link.proTitle')}</p>
          <p className="mt-1 text-[12.5px] leading-relaxed text-attention-foreground/85">
            {t('link.proBody', { name: displayName })}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              onClick={() => useUpgradeDialogStore.getState().open('progressLink')}
            >
              {t('link.proCta')}
            </Button>
            <a
              href={demoProgressUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[12.5px] font-medium text-brand-600 hover:underline"
            >
              {t('link.sample')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (hasOtherError) {
    return errorBox(() => refetch());
  }

  // The plan allows the link, so the query is enabled and `isPending` is a
  // real in-flight signal (a disabled query would sit on pending forever, but
  // that case returned above as `locked`). Rendering the "chưa có link" box
  // here would flash a Tạo link button at every student on every cold load.
  if (linkPending) {
    return (
      <div>
        {heading}
        <div className="mt-1.5 space-y-2 rounded-md border border-surface-200 p-4" aria-busy="true">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
    );
  }

  if (!link) {
    return (
      <div>
        {heading}
        <div className="mt-1.5 rounded-md border border-dashed border-brand-300 p-4">
          <p className="text-[12.5px] leading-relaxed text-surface-600">
            {t('link.descNew', { name: displayName })}
          </p>
          <AdminOnlyTooltip show={!canManage}>
            <Button
              size="sm"
              className="mt-3 h-[44px] w-full sm:h-9"
              disabled={!canManage || create.isPending}
              onClick={() => createAndCopy(false)}
            >
              {t('link.create')}
            </Button>
          </AdminOnlyTooltip>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-surface-200 bg-surface-0 px-5 py-4">
      <div className="flex items-center gap-2.5">
        <BookHeart className="h-4 w-4 text-brand-600" strokeWidth={1.8} />
        <span className="text-sm font-semibold text-surface-800">{t('link.title')}</span>
        {/* THI-192 §12.1: `success` (brand-100/brand-800), matching CD's tint.
            It was `muted`, which reads as "inactive" for a state that means
            the page is live. */}
        <Badge variant="success" className="ml-auto">{t('link.open')}</Badge>
      </div>
      <p className="mt-2 text-[12.5px] leading-[1.45] text-surface-500">
        {t('link.descCreated', { name: displayName })}
      </p>
      <div className="mt-3 flex h-[33.6px] items-center gap-2 overflow-hidden rounded-sm border border-surface-200 bg-surface-50 px-3">
        {/* Display only. `copy()` above and the ⋯ menu's <a href> below both
            still use `link.url` — a scheme-less string is not a link. */}
        <span className="truncate font-mono text-xs text-surface-600">{displayUrl(link.url)}</span>
      </div>
      <div className="mt-2.5 flex gap-2">
        <Button className="h-[44px] flex-1 gap-2 sm:h-[33.6px]" onClick={() => void copy(link.url)}>
          <Copy className="h-[14px] w-[14px]" strokeWidth={2} />
          {t('link.copy')}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              aria-label={t('link.title')}
              className="h-[44px] w-[44px] shrink-0 sm:h-[33.6px] sm:w-[33.6px]"
            >
              <MoreHorizontal className="h-4 w-4" strokeWidth={1.8} />
            </Button>
          </DropdownMenuTrigger>
          {/* An explicit rem width: portalled content renders on the OUTSIDE
              --spacing scale, so a scale width comes out ~20% short
              (INVARIANTS §6). */}
          <DropdownMenuContent align="end" className="w-[15rem]">
            <DropdownMenuItem asChild>
              <a href={link.url} target="_blank" rel="noopener noreferrer">{t('link.openPage')}</a>
            </DropdownMenuItem>
            <AdminOnlyTooltip show={!canManage} className="block">
              <DropdownMenuItem
                disabled={!canManage}
                onClick={() => setRegenOpen(true)}
                className="text-destructive focus:text-destructive"
              >
                {t('link.regenerate')}
              </DropdownMenuItem>
            </AdminOnlyTooltip>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {/* Two hint lines (mock frame 1): the Zalo-paste nudge, and the ⋯ menu
          pointer — `link.hint2` (Task 3) was missing from this render before. */}
      <p className="mt-2.5 text-[11.5px] leading-[1.45] text-surface-400">
        {t('link.hint')} {t('link.hint2')}
      </p>

      {/* Frame 2's confirm box: a bottom Drawer on phone, the plain
          AlertDialog on desktop — same copy and destructive-first button
          order either way (DrawerFooter doesn't reverse order the way
          AlertDialogFooter does, so the destructive button is listed FIRST
          here to land on top of the stacked column). */}
      {isMobile ? (
        <Drawer open={regenOpen} onOpenChange={setRegenOpen}>
          <DrawerContent>
            <DrawerHeader className="text-left">
              <DrawerTitle>{t('link.regenTitle')}</DrawerTitle>
              <DrawerDescription>{t('link.regenBody')}</DrawerDescription>
            </DrawerHeader>
            <DrawerFooter>
              <Button
                className="h-[44px] bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={create.isPending}
                onClick={() => createAndCopy(true)}
              >
                {t('link.regenConfirm')}
              </Button>
              <DrawerClose asChild>
                <Button variant="outline" className="h-[44px]">
                  {t('link.regenCancel')}
                </Button>
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      ) : (
        <AlertDialog open={regenOpen} onOpenChange={setRegenOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('link.regenTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('link.regenBody')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('link.regenCancel')}</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={create.isPending}
                onClick={() => createAndCopy(true)}
              >
                {t('link.regenConfirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
